import { FC, SyntheticEvent, useMemo, useState, lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { IconButton } from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';

import { AccessLevel } from '@playq/octopus2-auth';

import { appToolkit, authToolkit } from '/store/toolkits';

import { corpMenuStructure } from './config';
import { IMenuItemBase } from './types';

const CorporateMenu = lazy(() =>
  import(/* webpackChunkName: "CorporateMenu" */ './CorporateMenu').then((module) => ({
    default: module.CorporateMenu,
  }))
);

export const Corporate: FC = () => {
  const access = useSelector(appToolkit.selectors.accessScope);
  const isAdmin = useSelector(authToolkit.selectors.isAdmin);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const visibleItems = useMemo(() => {
    return corpMenuStructure.filter((item) => {
      if (!item.module) return true;
      const moduleAccess = access[item.module];
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      return isAdmin || (!!moduleAccess && moduleAccess !== AccessLevel.None);
    });
  }, [access, isAdmin]);

  const handleClick = (event: SyntheticEvent) => {
    setAnchorEl(event.currentTarget as HTMLButtonElement);
  };

  const handleClose = (item?: IMenuItemBase) => (event: SyntheticEvent & { metaKey?: boolean }) => {
    if (event.metaKey) return;
    if (item?.disabled) {
      event.preventDefault();
      return;
    }
    setAnchorEl(null);
  };

  if (!visibleItems.length) {
    return null;
  }

  return (
    <>
      <IconButton
        color='inherit'
        aria-owns={anchorEl ? 'simple-menu' : undefined}
        aria-haspopup='true'
        aria-label='Corporate section'
        data-testid='button-corp-section'
        onClick={handleClick}
        size='large'
      >
        <BusinessIcon />
      </IconButton>

      {Boolean(anchorEl) && (
        <Suspense>
          <CorporateMenu anchorEl={anchorEl} onClose={handleClose} items={visibleItems} />
        </Suspense>
      )}
    </>
  );
};
